import React, { useState, useEffect } from 'react';
import { Package, CreditCard, CheckCircle, Upload, Truck, Download, PartyPopper, AlertTriangle, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

const DeliveryTimeline = ({ deliveryId }) => {
  const [timeline, setTimeline] = useState([]);
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTimeline();
  }, [deliveryId]);

  const loadTimeline = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/pickup/deliveries/${deliveryId}/timeline`,
        {
          method: 'GET',
          credentials: 'include'
        }
      );

      if (!response.ok) throw new Error('Failed to load timeline');

      const data = await response.json();
      setTimeline(data.timeline || []);
      setDeliveryInfo(data.delivery_info);
    } catch (error) {
      console.error('Load timeline error:', error);
      toast.error('Failed to load delivery timeline');
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (icon) => {
    const iconMap = {
      '📦': <Package className="h-5 w-5" />,
      '💳': <CreditCard className="h-5 w-5" />,
      '✅': <CheckCircle className="h-5 w-5" />,
      '📤': <Upload className="h-5 w-5" />,
      '🚚': <Truck className="h-5 w-5" />,
      '📥': <Download className="h-5 w-5" />,
      '🎉': <PartyPopper className="h-5 w-5" />,
      '⚠️': <AlertTriangle className="h-5 w-5" />,
      '❌': <XCircle className="h-5 w-5" />
    };
    return iconMap[icon] || <Package className="h-5 w-5" />;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      posted: 'bg-blue-500',
      payment: 'bg-green-500',
      accepted: 'bg-purple-500',
      pickup_confirmed: 'bg-orange-500',
      in_transit: 'bg-yellow-500',
      delivered: 'bg-teal-500',
      completed: 'bg-emerald-500',
      disputed: 'bg-red-500',
      cancelled: 'bg-gray-500'
    };
    return colorMap[status] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent mx-auto"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Delivery Timeline</span>
          {deliveryInfo && (
            <Badge variant="outline">
              #{deliveryInfo.delivery_code}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {timeline.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">No timeline data available</p>
        ) : (
          <div className="space-y-6">
            {/* Delivery Route Info */}
            {deliveryInfo && (
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex items-center justify-between text-sm">
                  <div>
                    <span className="font-medium">{deliveryInfo.pickup_city}</span>
                    <span className="text-muted-foreground"> → </span>
                    <span className="font-medium">{deliveryInfo.destination_city}</span>
                  </div>
                  <Badge>{deliveryInfo.package_type}</Badge>
                </div>
              </div>
            )}

            {/* Timeline */}
            <div className="relative">
              {/* Vertical Line */}
              <div className="absolute left-[21px] top-8 bottom-8 w-0.5 bg-gray-200 dark:bg-gray-700"></div>

              {/* Timeline Items */}
              <div className="space-y-6">
                {timeline.map((item, index) => (
                  <div key={index} className="relative flex gap-4">
                    {/* Icon Circle */}
                    <div className={`relative z-10 flex items-center justify-center w-11 h-11 rounded-full ${getStatusColor(item.status)} text-white shadow-lg`}>
                      {getIcon(item.icon)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 pt-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold">{item.title}</h4>
                          <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                        </div>
                        <time className="text-xs text-muted-foreground">
                          {new Date(item.timestamp).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </time>
                      </div>

                      {/* Additional Details Card */}
                      {index === 0 && (
                        <div className="mt-3 p-3 bg-white dark:bg-gray-800 border rounded-lg text-sm">
                          <div className="font-medium text-muted-foreground">Latest Update</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Status Summary */}
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {timeline.length} status {timeline.length === 1 ? 'update' : 'updates'}
                </span>
                <button
                  onClick={loadTimeline}
                  className="text-primary hover:underline"
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DeliveryTimeline;
